import type maplibregl from 'maplibre-gl';
import { TerraDraw, TerraDrawLineStringMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import type { LonLat } from '../api/client';

const ROUTE_FARBE = '#1D4F91';

/**
 * Terra-Draw-Anbindung für genau eine Route (Linie): zeichnen, Punkte verschieben, Zwischenpunkte einfügen.
 * Meldet jede abgeschlossene Änderung über onAenderung (Autosave übernimmt der Aufrufer).
 */
export class RouteZeichner {
  private readonly draw: TerraDraw;
  private letzteMeldung = '';
  private aenderungsTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    map: maplibregl.Map,
    private readonly onAenderung: (coords: LonLat[]) => void,
    private readonly onGezeichnet: () => void,
  ) {
    this.draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawSelectMode({
          flags: {
            linestring: {
              feature: {
                draggable: false,
                coordinates: { midpoints: true, draggable: true, deletable: true },
              },
            },
          },
          styles: {
            selectedLineStringColor: ROUTE_FARBE,
            selectedLineStringWidth: 5,
            selectionPointColor: ROUTE_FARBE,
            selectionPointWidth: 7,
            midPointColor: '#FFFFFF',
            midPointOutlineColor: ROUTE_FARBE,
          },
        }),
        new TerraDrawLineStringMode({
          styles: {
            lineStringColor: ROUTE_FARBE,
            lineStringWidth: 5,
            closingPointColor: ROUTE_FARBE,
          },
        }),
      ],
    });
    this.draw.start();
    this.draw.setMode('select');

    this.draw.on('finish', (id, ctx) => {
      if (ctx.mode === 'linestring' && ctx.action === 'draw') {
        // Nur eine Route je Planversion: ältere Linien entfernen.
        const alte = this.linien()
          .map((f) => f.id)
          .filter((fid): fid is string | number => fid !== undefined && fid !== id);
        if (alte.length > 0) this.draw.removeFeatures(alte);
        this.draw.setMode('select');
        this.draw.selectFeature(id);
        this.onGezeichnet();
      }
      this.melden();
    });
    // Löschen/Verschieben im Auswahlmodus löst nicht immer "finish" aus – Änderungen gesammelt melden.
    this.draw.on('change', () => {
      if (this.draw.getMode() !== 'select') return;
      clearTimeout(this.aenderungsTimer);
      this.aenderungsTimer = setTimeout(() => this.melden(), 400);
    });
  }

  zeichnen(): void {
    this.draw.setMode('linestring');
  }

  auswaehlen(): void {
    if (this.draw.getMode() !== 'select') this.draw.setMode('select');
  }

  /** Route von außen setzen (Laden aus der API), ohne eine Änderung zu melden. */
  setzen(coords: LonLat[] | null): void {
    const schluessel = coords ? JSON.stringify(coords) : '';
    if (schluessel === this.letzteMeldung) return;
    const ids = this.linien()
      .map((f) => f.id)
      .filter((fid): fid is string | number => fid !== undefined);
    if (ids.length > 0) this.draw.removeFeatures(ids);
    if (coords && coords.length >= 2) {
      this.draw.addFeatures([
        {
          id: crypto.randomUUID(),
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: { mode: 'linestring' },
        },
      ]);
    }
    this.letzteMeldung = schluessel;
  }

  beenden(): void {
    clearTimeout(this.aenderungsTimer);
    this.draw.stop();
  }

  private linien() {
    return this.draw.getSnapshot().filter((f) => f.geometry.type === 'LineString');
  }

  private melden(): void {
    const linie = this.linien()[0];
    if (!linie || linie.geometry.type !== 'LineString') return;
    const coords = (linie.geometry.coordinates as number[][]).map(
      (p) => [round7(p[0] ?? 0), round7(p[1] ?? 0)] as LonLat,
    );
    if (coords.length < 2) return;
    const schluessel = JSON.stringify(coords);
    if (schluessel === this.letzteMeldung) return;
    this.letzteMeldung = schluessel;
    this.onAenderung(coords);
  }
}

function round7(v: number): number {
  return Math.round(v * 1e7) / 1e7;
}
